<template>
  <v-expansion-panel class="flash-betting-holder" v-model="computedExpansionPanel">
    <v-expansion-panel-content>
      <template v-slot:header>
        <div class="flash-betting-expansion-header">
          <div class="left-section">
            <span class="flash-betting-icon"></span>
            <span class="flash-betting-title">{{ $t('FlashBetting.Title') }}</span>
          </div>
        </div>
      </template>
      <v-card v-if="hasFlashMarkets" class="flash-betting-markets">
        <div
          v-for="(market, index) in visibleMarkets.filter(Boolean)"
          :key="market.idfomarket || 'market-' + index"
          class="flash-betting-container"
          :class="{'skipping': skippedMarket === market.idfomarket}"
          :style="cardStyles[index]"
          @transitionend="resetSkip(market.idfomarket)"
        >
          <div class="flash-betting-markets-header">
            <span class="flash-betting-indicator"></span>
            <span class="flash-betting-skip" @click="skip(market.idfomarket)">SKIP</span>
          </div>
          <div class="flash-betting-markets-title">{{ market.name }}</div>
          <!--    constraint text      -->
          <div v-if="market && market.externaldescription && market.externaldescription !== market.name"
               class="constraint-text"
          >
            {{ market.externaldescription }}
          </div>
          <markets :event="flashBettingMarkets" :markets="[market]" :hasFlashBettingMarket="true"></markets>
          <!--    contextual text      -->
          <div v-if="market && market.webnote" class="context-note">{{ market.webnote }}</div>
        </div>
      </v-card>
      <div v-else class="currently-empty">
        <div class="currently-empty-container">
          <div class="currently-empty-icon"></div>
        </div>
        <div class="currently-empty-text">{{ $t('FlashBetting.Empty') }}</div>
      </div>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>


<script>
import BettingOffer from '@/components/mixins/BettingOffer'
import markets from '@/components/sports/bettingoffer/markets'
import config from '@/config'
import router from '@/router'

export default {
  name: 'FlashBetting',
  data () {
    return {
      expansionPanel: 0,
      skippedMarket: null, // Track which market is being skipped
      visibleIndex: 0 // Index of the first visible card
    }
  },
  components: {
    markets
  },
  mixins: [BettingOffer],
  props: ['flashBettingMarkets'],
  computed: {
    computedExpansionPanel: {
      get () {
        return Array.isArray(this.flashBettingMarkets) && this.flashBettingMarkets.length
          ? 0
          : undefined
      },
      set (value) {
        this.expansionPanel = value
      }
    },
    hasFlashMarkets () {
      return Array.isArray(this.flashBettingMarkets) && this.flashBettingMarkets.length > 0
    },
    visibleMarkets () {
      if (!Array.isArray(this.flashBettingMarkets)) {
        return []
      }

      const markets = this.addNewMarketToTheTopOfTheStack
        ? this.flashBettingMarkets.slice().reverse()
        : this.flashBettingMarkets.slice()

      const maxStartIndex = Math.max(0, markets.length - 1)
      const safeIndex = Math.min(this.visibleIndex, maxStartIndex)

      // Debug mode => ?debug=true
      if (
        typeof router !== 'undefined' &&
        router.currentRoute &&
        router.currentRoute.query &&
        router.currentRoute.query.debug
      ) {
        console.log('%c[flashBettingMarkets]', 'color:yellow; font-weight: bold', {
          total: markets.length,
          hasNonTradableMarkets: markets.some(m => m.istradable === false),
          selections: markets.map(m => m.selections),
          markets: markets
        })
      }

      return markets.slice(safeIndex, safeIndex + 3)
    },
    cardStyles () {
      return this.visibleMarkets.map((_, index) => this.getCardStyles(index))
    },
    addNewMarketToTheTopOfTheStack () {
      return config.app.autoconf.FLASH_BETTING.addNewMarketToTheTopOfTheStack
    }
  },
  methods: {
    skip (idfomarket) {
      this.skippedMarket = idfomarket

      const marketsLength = Array.isArray(this.flashBettingMarkets)
        ? this.flashBettingMarkets.length
        : 0

      setTimeout(() => {
        if (marketsLength > 0) {
          this.visibleIndex = (this.visibleIndex + 1) % marketsLength
        }
        this.skippedMarket = null
      }, 300)
    },

    resetSkip (idfomarket) {
      if (this.skippedMarket === idfomarket) {
        this.skippedMarket = null
      }
    },

    getCardStyles (index) {
      const offsets = [-20, -10, 0]
      const offsetsWidth = [0, 15, 30]
      const translateY = offsets[index] || 0

      const markets = this.visibleMarkets && Array.isArray(this.visibleMarkets)
        ? this.visibleMarkets
        : []

      const isSkipping =
        markets[index] &&
        markets[index].idfomarket === this.skippedMarket

      const translateX = isSkipping ? '100%' : '0'

      return {
        zIndex: 100 - index,
        transform: `translateY(${translateY}px) translateX(${translateX})`,
        opacity: isSkipping ? 0 : 1,
        width: `calc(100% - ${offsetsWidth[index] || 0}px)`,
        margin: `0 auto`,
        transition: 'transform 0.3s ease-out, opacity 0.3s ease-out, width 0.3s ease-out, margin 0.3s ease-out'
      }
    }
  }
}
</script>

<style scoped lang="stylus">

  .flash-betting-holder
    background var(--flesh-betting-container-bgr)
    >>>.v-expansion-panel__container
        .v-expansion-panel__header
          background var(--coupon-header-markets-bgr)
          margin-bottom 10px
          min-height 40px
          .flash-betting-expansion-header
            .left-section
              margin-left 10px
              display flex
              align-items center
          padding 0
          .flash-betting-icon
            background url("~@/assets/images/icons/flash-betting-icon.svg") 100% center no-repeat
            width 27px
            height 20px
            display inline-flex
            margin-right 10px
          .flash-betting-title
            font-family: 'Roboto Condensed Bold'
            font-size: 16px
            font-weight: 500
            line-height: 18.75px
            text-align: left
            text-underline-position: from-font
            text-decoration-skip-ink: none
            color var(---coupon-header-markets-color)
          .v-icon
            color var(--coupon-header-markets-color) !important
        .v-expansion-panel__body
          display flex
          justify-content center
          height 225px
          position relative
          z-index 1
          .flash-betting-markets
            padding-right 5px
            width 95%
            display flex
            flex-direction column
            align-items center
            margin-top 18px
            margin-bottom 10px
            position relative
            background var(--flesh-betting-container-bgr)
            .flash-betting-container
              box-shadow: var(--flesh-betting-container-box-shadow)
              max-height 195px
              min-height 195px
              background var(--flesh-betting-container-bgr)
              width 100%
              display flex
              flex-direction column
              justify-content space-evenly
              flex: 0 0 auto
              border-radius: 10px
              position absolute
              top 0
              left 0
              right 0
              clip-path: inset(0) // Adjust the visible area of the container
              transition: clip-path 0.3s ease-out, transform 0.3s ease-out, opacity 0.3s ease-out
              .flash-betting-markets-title
                font-family: 'Roboto Condensed Bold'
                font-size: 17px
                font-weight: 700
                line-height: 23.44px
                padding: 0 10px
                text-align: center
                max-height: 46px
                overflow: hidden
                display: -webkit-box
                -webkit-box-orient: vertical
                -webkit-line-clamp: 2
                text-overflow: ellipsis
                margin-bottom 5px
                color var(--flesh-betting-container-color)
              .constraint-text
                text-align center
                color var(--flesh-betting-container-color)
              .context-note
                text-align center
                display flex
                align-items center
                justify-content center
                margin-bottom 3px
                color  var(--flesh-betting-container-color)
              .flash-betting-markets-header
                display flex
                justify-content space-between
                align-items center
                position relative
                padding 0px 10px 0 10px
                .flash-betting-indicator
                  background url("~@/assets/images/icons/flash-betting-icon.svg") 100% center no-repeat
                  width 27px
                  height 20px
                  display inline-flex
                  margin-right 10px
                  position absolute
                  left 50%
                  transform translateX(-50%)
                .flash-betting-skip
                  margin-left auto
                  font-family: 'Roboto Condensed'
                  font-size 15px
                  font-weight 400
                  line-height 17.58px
                  text-align left
                  text-underline-position from-font
                  text-decoration-skip-ink none
                  color #838CA1
                  cursor pointer
          .currently-empty
            display flex
            width 95%
            box-shadow var(--flesh-betting-container-box-shadow)
            border-radius 10px
            margin 5px 5px 10px 5px
            flex-direction column
            justify-content center
            .currently-empty-container
              display flex
              justify-content center
              align-items center
              .currently-empty-icon
                background url("~@/assets/images/icons/flash-betting-currently-empty.gif") no-repeat
                width 120px
                height 100px
                margin auto
                display block
            .currently-empty-text
              font-family 'Roboto Condensed Bold'
              color #CCCFD2
              font-size 32px
              display flex
              justify-content center

</style>



// WEBPACK FOOTER //
// src/components/sports/bettingoffer/FlashBetting.vue